/** @jsx jsx */
import { jsx } from '@emotion/react';

import * as React from 'react';
import { useEffect, useRef, useState } from 'react';

import { Grid } from '@coursera/cds-core';
import { AIGenerateIcon } from '@coursera/cds-icons';

import CoachIcon from 'bundles/ai-coach-platform/components/branding/CoachIcon';
import { ToolbarButton } from 'bundles/ai-course-coach/components/common/ToolbarButton';
import { styles } from 'bundles/course-v2/components/navigation/CourseToolBar/CourseToolBarStyles';
import type { Plugin, ToolIconRecord } from 'bundles/course-v2/components/navigation/CourseToolBar/CourseToolBarTypes';
import ToolbarErrorBoundary from 'bundles/course-v2/components/navigation/CourseToolBar/ToolbarErrorBoundary';

import _t from 'i18n!nls/learning-assistant-chat-ai';

import { useCourseToolBarPlugins } from './useCourseToolBarPlugins';

const toolsOrder = ['coach'];

// in case there are no tools registered, these are the defaults
// these icons would render in disabled state, and not be functional.
const defaultToolConfiguration: ToolIconRecord = {
  // this entry is only for the purpose of showing a disabled coach icon
  // when the plugin itself is not registered. we want a consistent icon
  // to show up, and in disabled state.
  coach: { enabled: false, icon: <CoachIcon size="medium" /> },
};

type CourseToolBarProps = { showToolBar: boolean };

const CourseToolBar: React.FC<CourseToolBarProps> = ({ showToolBar = false }) => {
  const { plugins, toolSelected, setToolSelected, expanded, setExpanded } = useCourseToolBarPlugins();
  const [iconState, setIconState] = useState<ToolIconRecord>(defaultToolConfiguration);
  const [currentToolOrder, setCurrentToolOrder] = useState(toolsOrder);

  const toolbarPanelContainerRef = useRef<HTMLDivElement | null>(null);

  const computeStyles = React.useCallback<(...args: $TSFixMe[]) => $TSFixMe>(styles, [showToolBar]);

  const updateToolState = React.useCallback<(...args: $TSFixMe[]) => $TSFixMe>(
    (pluginMap: Record<string, Plugin>, modifiedToolList: string[]) => {
      const updatedToolIcons: ToolIconRecord = defaultToolConfiguration;

      modifiedToolList.forEach((tool) => {
        if (updatedToolIcons[tool]) {
          updatedToolIcons[tool].enabled = !!pluginMap[tool];
          updatedToolIcons[tool].icon = pluginMap[tool]?.icon;
        } else {
          updatedToolIcons[tool] = {
            enabled: !!pluginMap[tool],
            icon: pluginMap[tool]?.icon || <AIGenerateIcon />,
          };
        }
      });

      setIconState(updatedToolIcons);
      setCurrentToolOrder(modifiedToolList);
    },
    []
  );

  useEffect(() => {
    // create a map of the plugins
    // this will
    // - dedupe any accidental duplicates if it happens
    // - allow us to quickly check if a tool is registered
    // - allow us to quickly get the icon for a tool
    const pluginMap: Record<string, Plugin> = Object.fromEntries(plugins.map((plugin) => [plugin.name, plugin]));

    // construct a new tools list based on the published plugins
    const modifiedToolList = Array.from(new Set([...toolsOrder, ...Object.keys(pluginMap)]));

    updateToolState(pluginMap, modifiedToolList);
  }, [plugins, updateToolState]);

  useEffect(() => {
    // programmatically brings focus to the toolbar content area when toolbar is opened
    if (expanded && !!toolSelected) {
      if (toolbarPanelContainerRef.current) {
        toolbarPanelContainerRef.current.focus();
      }
    }
  }, [expanded, toolSelected]);

  const handleSelectItem = (tool: string) => {
    setToolSelected(tool);
    setExpanded(toolSelected !== tool || !expanded);
  };

  const computedStyles = computeStyles(showToolBar);

  const selectedPlugin = plugins.find((plugin) => plugin.name === toolSelected);
  const isToolbarClosed = !expanded;

  return (
    <ToolbarErrorBoundary>
      <Grid
        data-testid="course-toolbar"
        container
        item
        css={[computedStyles.toolbarContainer, isToolbarClosed && computedStyles.closed]}
        direction="row"
        role="toolbar"
        aria-hidden={isToolbarClosed} // hide from screen-reader users when closed
      >
        {plugins.length >= 1 && (
          <Grid item css={computedStyles.toolbarContent} data-testid="course-toolbar-content">
            {selectedPlugin && (
              // TODO: re-enable focus-trap
              <div
                role="complementary"
                aria-roledescription="sidebar"
                ref={toolbarPanelContainerRef}
                tabIndex={-1}
                aria-label={_t('Tool container for #{toolName}', { toolName: selectedPlugin.name })}
              >
                {selectedPlugin.component}
              </div>
            )}
          </Grid>
        )}

        {showToolBar && (
          <Grid item css={computedStyles.toolbarPanel} role="complementary" aria-label={_t('Toolbar panel')}>
            <div role="tablist" tabIndex={0}>
              {currentToolOrder.map((toolName) => {
                const selected = iconState[toolName]?.enabled;
                return (
                  <ToolbarButton
                    role="tab"
                    key={toolName}
                    aria-label={toolName}
                    onClick={() => handleSelectItem(toolName)}
                    icon={iconState[toolName]?.icon}
                    disabled={!selected}
                    data-testid={`tool-button-${toolName}`}
                    aria-selected={selected}
                  >
                    {toolName}
                  </ToolbarButton>
                );
              })}
            </div>
          </Grid>
        )}
      </Grid>
    </ToolbarErrorBoundary>
  );
};

export default CourseToolBar;
